<template>
  <div class="container-fluid py-4">
    <!-- Breadcrumb and Title -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center">
          <router-link to="/bonus-list" class="text-decoration-none"></router-link>
          <span class="text-muted me-2">Bonus Details</span>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="row">
      <div class="col-12 text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">Loading bonus details...</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="row">
      <div class="col-12">
        <div class="alert alert-danger" role="alert">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Main -->
    <div v-else class="row">
      <div class="col-12">
        <!-- Header card -->
        <div class="card mb-4 shadow-sm">
          <div class="card-header cap-header mx-3 mt-3 d-flex align-items-center">
            <h6 class="text-white text-capitalize m-0">
              Bonus Details - {{ bonusTitle || '—' }}
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <p class="mb-0"><strong>Title:</strong> {{ bonusTitle || '—' }}</p>
              </div>
              <div class="col-md-6">
                <p class="mb-0"><strong>Date:</strong> {{ bonusDate || '—' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Details table -->
        <div class="card shadow-sm">
          <div class="card-header cap-header mx-3 mt-3 d-flex align-items-center">
            <h6 class="text-white text-capitalize m-0">
              Employee Bonus Details ({{ bonusDetails.length }} Employees)
            </h6>
          </div>

          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0">Project</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0">Employee</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0 text-center">Max bonus</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0 text-center">Bonus amount</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0 text-center">Other bonus</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0 text-center">Percentage</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0 text-center">Total</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="detail in bonusDetails"
                    :key="detail.id"
                    class="border-bottom"
                  >
                    <td class="px-4 py-3">
                      <span class="text-muted">{{ detail.project }}</span>
                    </td>
                    <td class="px-4 py-3">
                      <span class="fw-medium">{{ detail.employee }}</span>
                    </td>

                    <td class="px-4 py-3 text-center">
                      <span class="text-muted">{{ formatNumber(detail.max_bonus) }}</span>
                    </td>

                    <!-- bonus_amount -->
                    <td class="px-4 py-3 text-center">
                      <div class="editable-container">
                        <input
                          v-if="editingKey === `bonus_amount-${detail.id}`"
                          ref="editInput"
                          v-model="editingValue"
                          type="number"
                          step="0.01"
                          min="0"
                          class="form-control inline-input"
                          @blur="saveField(detail.id, 'bonus_amount')"
                          @keyup.enter="saveField(detail.id, 'bonus_amount')"
                          @keyup.escape="cancelEdit"
                        />
                        <div
                          v-else
                          class="display-pill"
                          :class="{ editable: !isUpdating }"
                          @click="startEdit(detail.id, 'bonus_amount', detail.bonus_amount)"
                        >
                          <span>{{ formatNumber(detail.bonus_amount) }}</span>
                          <i v-if="!isUpdating" class="fas fa-edit edit-icon ms-2 text-muted"></i>
                        </div>
                      </div>
                    </td>

                    <!-- other_bonus -->
                    <td class="px-4 py-3 text-center">
                      <div class="editable-container">
                        <input
                          v-if="editingKey === `other_bonus-${detail.id}`"
                          ref="editInput"
                          v-model="editingValue"
                          type="number"
                          step="0.01"
                          min="0"
                          class="form-control inline-input"
                          @blur="saveField(detail.id, 'other_bonus')"
                          @keyup.enter="saveField(detail.id, 'other_bonus')"
                          @keyup.escape="cancelEdit"
                        />
                        <div
                          v-else
                          class="display-pill"
                          :class="{ editable: !isUpdating }"
                          @click="startEdit(detail.id, 'other_bonus', detail.other_bonus)"
                        >
                          <span>{{ formatNumber(detail.other_bonus) }}</span>
                          <i v-if="!isUpdating" class="fas fa-edit edit-icon ms-2 text-muted"></i>
                        </div>
                      </div>
                    </td>

                    <!-- mark_percent -->
                    <td class="px-4 py-3 text-center">
                      <div class="editable-container">
                        <input
                          v-if="editingKey === `mark_percent-${detail.id}`"
                          ref="editInput"
                          v-model="editingValue"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          class="form-control inline-input"
                          @blur="saveField(detail.id, 'mark_percent')"
                          @keyup.enter="saveField(detail.id, 'mark_percent')"
                          @keyup.escape="cancelEdit"
                        />
                        <div
                          v-else
                          class="display-pill"
                          :class="{ editable: !isUpdating }"
                          @click="startEdit(detail.id, 'mark_percent', detail.mark_percent)"
                        >
                          <span>{{ formatNumber(detail.mark_percent) }}</span>
                          <i v-if="!isUpdating" class="fas fa-edit edit-icon ms-2 text-muted"></i>
                        </div>
                      </div>
                    </td>

                    <!-- total -->
                    <td class="px-4 py-3 text-center">
                      <span>{{ formatNumber(calcTotal(detail)) }}</span>
                    </td>
                  </tr>

                  <tr v-if="bonusDetails.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      No bonus details found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Footer actions -->
        <div class="row mt-4">
          <div class="col-12 text-end">
            <router-link to="/bonus-list" class="btn btn-outline-secondary me-2">
              <i class="fas fa-arrow-left me-1"></i>
              Back to List
            </router-link>
            <button
              class="btn btn-cap"
              @click="saveAllChanges"
              :disabled="!hasChanges || isUpdating"
            >
              <i v-if="isUpdating" class="fas fa-spinner fa-spin me-1"></i>
              <i v-else class="fas fa-save me-1"></i>
              {{ isUpdating ? 'Saving...' : 'Save All Changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import axios from '@/api'

const route = useRoute()
const toast = useToast()

// state
const loading = ref(true)
const error = ref('')
const bonusDetails = ref([])

const bonusTitle = ref('')
const bonusDate = ref('')

const editingKey = ref(null)       // e.g. "bonus_amount-3"
const editingValue = ref('')
const isUpdating = ref(false)
const hasChanges = ref(false)
const editInput = ref(null)

const bonusId = route.params.id

// fetch details + header
const fetchBonusDetails = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await axios.get(`/financial-requests/monthly-bonuses/${bonusId}/details`)
    bonusDetails.value = res.data?.data || []

    const headRes = await axios.get('/financial-requests/monthly-bonuses')
    const item = (headRes.data?.data || []).find(s => String(s.id) === String(bonusId))
    if (item) {
      bonusTitle.value = item.title || 'Unknown'
      bonusDate.value = item.date || 'Unknown'
    }
  } catch (e) {
    console.error('load bonus details error:', e)
    error.value = 'Failed to load bonus details. Please try again.'
  } finally {
    loading.value = false
  }
}

// start edit
const startEdit = (detailId, field, currentValue) => {
  if (isUpdating.value) return
  editingKey.value = `${field}-${detailId}`
  editingValue.value = String(currentValue ?? 0)
  nextTick(() => {
    if (editInput.value && editInput.value[0]) {
      editInput.value[0].focus()
      editInput.value[0].select()
    }
  })
}

const cancelEdit = () => {
  editingKey.value = null
  editingValue.value = ''
}

// save a single field
const saveField = async (detailId, field) => {
  const num = parseFloat(editingValue.value)
  if (Number.isNaN(num) || num < 0 || (field === 'mark_percent' && (num < 0 || num > 100))) {
    toast.error('Please enter a valid value')
    return
  }

  isUpdating.value = true
  try {
    const payload = { [field]: num }
    await axios.patch(`/financial-requests/monthly-bonuses/update-details/${detailId}`, payload)

    // local update
    const idx = bonusDetails.value.findIndex(d => d.id === detailId)
    if (idx !== -1) {
      bonusDetails.value[idx][field] = num
    }
    hasChanges.value = true
    toast.success('Value updated successfully')
  } catch (e) {
    console.error('update bonus field error:', e)
    toast.error('Failed to update value')
  } finally {
    isUpdating.value = false
    cancelEdit()
  }
}

// Save all (placeholder)
const saveAllChanges = () => {
  toast.success('All changes have been saved!')
  hasChanges.value = false
}

// helpers
const formatNumber = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2 })
    .format(parseFloat(amount) || 0)

const calcTotal = (d) =>
  (parseFloat(d.bonus_amount) || 0) + (parseFloat(d.other_bonus) || 0)

onMounted(fetchBonusDetails)
</script>

<style scoped>
/* CAP red band (same spirit as SalaryDetails/Contracts) */
.cap-header {
  background: linear-gradient(195deg, #b03a45, #8d2e38);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 0.75rem 1rem;
}

/* CAP red button */
.btn-cap {
  background-color: #b03a45;
  border-color: #b03a45;
  color: #fff;
}
.btn-cap:hover {
  background-color: #8d2e38;
  border-color: #8d2e38;
  color: #fff;
}
.btn-cap:disabled {
  opacity: 0.65;
  pointer-events: none;
}

/* Inline edit UI (like SalaryDetails) */
.editable-container { position: relative; min-height: 30px; }
.display-pill {
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 110px;
  border: 1px solid transparent;
  background-color: #f8f9fa;
}
.display-pill.editable:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}
.inline-input {
  width: 140px;
  padding: 6px 12px;
  border: 2px solid #b03a45;
  border-radius: 6px;
  font-weight: 500;
}
.inline-input:focus {
  outline: none;
  border-color: #8d2e38;
  box-shadow: 0 0 0 0.2rem rgba(176, 58, 69, 0.25);
}

.edit-icon { opacity: 0; transition: opacity 0.2s ease; font-size: 12px; }
.display-pill.editable:hover .edit-icon { opacity: 0.6; }

/* Card & table cosmetics (same as SalaryDetails) */
.card { border: none; border-radius: 12px; }
.shadow-sm { box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important; }
.table th {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}
.table td { vertical-align: middle; }
</style>
